import React from 'react';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import type { SystemAnnouncement } from '../data/systemAnnouncements';

type SystemAnnouncementCardProps = {
  announcement: SystemAnnouncement;
  eyebrow: string;
  title: string;
  body: string;
  primaryLabel: string;
  secondaryLabel?: string;
  dismissLabel: string;
  onPrimary: () => void;
  onSecondary?: () => void;
  onDismiss: () => void;
};

const SystemAnnouncementCard: React.FC<SystemAnnouncementCardProps> = ({
  body,
  dismissLabel,
  eyebrow,
  onDismiss,
  onPrimary,
  onSecondary,
  primaryLabel,
  secondaryLabel,
  title,
}) => (
  <div className="sl-system-announcement my-4 flex justify-start">
    <div className="w-full max-w-[42rem] rounded-lg border border-amber-200/80 bg-white/95 p-3 shadow-[0_16px_45px_-28px_rgba(15,23,42,0.45)] ring-1 ring-slate-950/[0.02] dark:border-amber-500/20 dark:bg-slate-900/95 dark:ring-white/[0.03]">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700 shadow-sm dark:bg-amber-500/15 dark:text-amber-300">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
            {eyebrow}
          </div>
          <div className="mt-1 text-sm font-semibold leading-snug text-slate-950 dark:text-slate-100">
            {title}
          </div>
          <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {body}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onPrimary}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-slate-950 px-3 text-xs font-semibold text-white transition-colors hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200 dark:focus-visible:ring-offset-slate-900"
            >
              {primaryLabel}
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            {secondaryLabel && onSecondary ? (
              <button
                type="button"
                onClick={onSecondary}
                className="inline-flex h-8 items-center rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 dark:focus-visible:ring-offset-slate-900"
              >
                {secondaryLabel}
              </button>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          aria-label={dismissLabel}
          title={dismissLabel}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

export default SystemAnnouncementCard;
